import { keepPreviousData, QueryFunction, QueryKey, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext, useEffect } from 'react';

import AppContext from '@/contexts/appContext';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

const useAPI = <TQueryFnData, TError = Error, TData = TQueryFnData>({
  enabled = true,
  queryKey,
  queryFn,
  select,
  staleTime,
  supressErrorMessages = false,
  refetchInterval,
  keepPreviousData: shouldKeepPreviousData = false
}: {
  enabled?: boolean;
  queryKey: QueryKey;
  queryFn: QueryFunction<TQueryFnData>;
  select?: (data: TQueryFnData) => TData;
  staleTime?: number;
  supressErrorMessages?: boolean;
  refetchInterval?: number | false;
  /**
   * When true, keeps the previous query's data displayed (and `isLoading`
   * false) while a new `queryKey` is fetching, instead of unmounting to a
   * pending state. Useful for paginated/range-based queries (e.g. calendars)
   * where the key changes on every navigation.
   */
  keepPreviousData?: boolean;
}) => {
  const { addMessage } = useContext(AppContext);

  const queryResponse = useQuery<TQueryFnData, TError, TData>({
    enabled,
    queryFn,
    queryKey,
    placeholderData: shouldKeepPreviousData ? keepPreviousData : undefined,
    retry: false,
    select,
    staleTime,
    refetchInterval
  });

  const { error, isError } = queryResponse;

  useEffect(() => {
    if (isError && !supressErrorMessages) {
      addMessage(mapAxiosErrorMessages(error as AxiosError));
    }
  }, [addMessage, error, isError, supressErrorMessages]);

  return queryResponse;
};

export default useAPI;
